import { useEffect, useId, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { useTranslation } from "react-i18next";
import {
  Button,
  ContentCard,
  Drawer,
  Field,
  ResponsiveFlex,
  Row,
} from "@narmi/design_system";
import { useSetChatVisibility } from "cerulean";
import M2M from "byzantine/src/M2M";
import { extractErrorMessages } from "byzantine/src/utils";
import type Account from "byzantine/src/Account";
import type MemberFriend from "byzantine/src/MemberFriend";
import type { Dollars } from "byzantine/src/shared";
import { useSudoContext } from "../../SudoContext";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import ViewMember from "./ViewMember";

type MemberFormData = {
  first_name: string;
  last_name: string;
  account_number: string;
  relationship: string;
};

type RequiredField = "first_name" | "last_name" | "account_number";

const EMPTY_FORM: MemberFormData = {
  first_name: "",
  last_name: "",
  account_number: "",
  relationship: "",
};

const REQUIRED_FIELDS: RequiredField[] = [
  "first_name",
  "last_name",
  "account_number",
];

export type MemberDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
} & (
  | {
      mode: "add";
      accounts: Account[];
      newMemberAdded: (id?: string, error?: string) => void;
    }
  | { mode: "view"; memberFriend: MemberFriend; onDelete: () => void }
);

const MemberDrawer = (props: MemberDrawerProps) => {
  const { isOpen, onClose } = props;
  const { l10n } = useLocalization();
  const drawerId = useId();
  useSetChatVisibility(drawerId, !isOpen);
  const { t } = useTranslation();
  const [formData, setFormData] = useState<MemberFormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<RequiredField, string>>>(
    {}
  );
  const [isLoading, setIsLoading] = useState(false);
  const isSubmittingRef = useRef(false);
  const { establishSudo, setDestinationRoute } = useSudoContext();
  const location = useLocation();
  const { sudo_mode_required_for_add_member: isSudoRequired } =
    useInstitutionSettings();

  useEffect(() => {
    if (props.mode === "add") setDestinationRoute(location.pathname);
  }, []);

  const updateField = (field: keyof MemberFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const closeDrawerAndResetForm = () => {
    onClose();
    setFormData(EMPTY_FORM);
    setErrors({});
  };

  const addMember = async () => {
    if (props.mode !== "add") return;
    const { accounts, newMemberAdded } = props;
    const newMember = new M2M({
      amount: 0 as Dollars,
      from_account_id: accounts[0].id,
      to_account_number: formData.account_number,
      first_name: formData.first_name,
      last_name: formData.last_name,
      relationship: formData.relationship,
    });
    try {
      const response = await newMember.addNewMemberFriend();
      newMemberAdded(response.member_friends[0].id);
      closeDrawerAndResetForm();
    } catch (error) {
      newMemberAdded(
        undefined,
        extractErrorMessages(error, l10n.getString("error-generic"))
      );
    }
  };

  const onSubmit = async () => {
    if (isSubmittingRef.current) return;
    const missingFields = REQUIRED_FIELDS.filter(
      (field) => !formData[field].trim()
    );
    if (missingFields.length) {
      setErrors(
        Object.fromEntries(
          missingFields.map((field) => [
            field,
            l10n.getString("error-required"),
          ])
        )
      );
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);
    try {
      if (isSudoRequired) {
        await establishSudo(location.pathname, addMember);
      } else {
        await addMember();
      }
    } catch {
      // the user cancelled verification, so the form stays as it was
    } finally {
      isSubmittingRef.current = false;
      setIsLoading(false);
    }
  };

  const getErrors = (field: RequiredField) =>
    errors[field] ? [errors[field] as string] : undefined;

  return (
    <Drawer
      isOpen={isOpen}
      onUserDismiss={props.mode === "view" ? onClose : closeDrawerAndResetForm}
      position="right"
      depth="633px"
      paddingSize="xl"
      showControls={false}
      testId={
        props.mode === "view" ? "view-member-drawer" : "add-member-drawer"
      }
      footer={
        props.mode === "view" ? (
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                kind="tertiary"
                testId="close-view-member-drawer"
                onClick={onClose}
                label={l10n.getString("button-close")}
              />
            </Row.Item>
          </Row>
        ) : (
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                kind="negative"
                testId="cancel-add-member-drawer"
                onClick={closeDrawerAndResetForm}
                disabled={isLoading}
                label={l10n.getString("button-cancel")}
              />
            </Row.Item>
            <Row.Item shrink>
              <Button
                kind="primary"
                testId="add-member-drawer-submit"
                onClick={onSubmit}
                disabled={isLoading}
                isLoading={isLoading}
                label={t(l10n.getString("member-add-button"))}
              />
            </Row.Item>
          </Row>
        )
      }
    >
      {props.mode === "view" ? (
        <ViewMember
          memberFriend={props.memberFriend}
          onDelete={props.onDelete}
        />
      ) : (
        <>
          <h2 className="margin--bottom--xl">
            {t(l10n.getString("member-add-label"))}
          </h2>
          <ContentCard kind="bordered" paddingSize="l">
            <ResponsiveFlex gapSize="m">
              <Field.Text
                label={l10n.getString("member-first-name-label")}
                value={formData.first_name}
                onChange={(value) => updateField("first_name", value)}
                errors={getErrors("first_name")}
                required
              />
              <Field.Text
                label={l10n.getString("member-drawer-last-name-label")}
                value={formData.last_name}
                onChange={(value) => updateField("last_name", value)}
                errors={getErrors("last_name")}
                required
              />
              <Field.Text
                label={l10n.getString("member-account-number-label")}
                value={formData.account_number}
                // member account numbers only allow letters, digits, "=" and "-"
                onChange={(value) =>
                  updateField(
                    "account_number",
                    value.replace(/[^a-zA-Z\d=-]/g, "")
                  )
                }
                errors={getErrors("account_number")}
                required
              />
              <Field.Text
                label={l10n.getString("member-drawer-relationship-label")}
                placeholder={l10n.getString(
                  "member-drawer-relationship-placeholder"
                )}
                value={formData.relationship}
                onChange={(value) => updateField("relationship", value)}
              />
            </ResponsiveFlex>
          </ContentCard>
        </>
      )}
    </Drawer>
  );
};

export default MemberDrawer;
